import { ButtonProps } from '@chakra-ui/react';
import { Dinero } from 'dinero.js';
import { RawDraftContentState } from 'draft-js';

import { TicketSelect_EventFragment } from '@/components/TicketedEventRegistrationButton/components/EventRegistrationModal/graphql/graphql-types/TicketSelect_EventFragment';
import { TicketSelect_EventFragment_ticketsCurrentUserCanPurchase } from '@/components/TicketedEventRegistrationButton/components/EventRegistrationModal/ticketTypes';
import { PaymentMethod_PaymentMethodFragment } from '@/components/graphql-types/PaymentMethod_PaymentMethodFragment';
import {
  AddItemToCartInput,
  CartItemType,
  CartItemTypeEnum,
  CreateCartOrderInput,
  GetCartInput,
  GetUserCartOrdersFilter,
  GetUserCartOrdersInput,
  OrderStatus,
  PlanTypeEnum,
  PromoCodeStatus,
  PromoCodeVisibility,
  UpdateItemMetadataInput,
  UpdateItemQuantityInput,
} from '@/graphql-types/globalTypes';

// ============================================================================
// ENUMS (Re-export from global types)
// ============================================================================

// Re-export enums from global types
export {
  CartItemType,
  CartItemTypeEnum,
  PlanTypeEnum,
  PromoCodeVisibility,
  PromoCodeStatus,
  OrderStatus,
};

// Cart state machine states (local to Cart component)
export enum CartState {
  INITIALIZING = 'INITIALIZING',
  LOADING = 'LOADING',
  LOADED_EMPTY = 'LOADED_EMPTY',
  LOADED_WITH_ITEMS = 'LOADED_WITH_ITEMS',
  UPDATING = 'UPDATING',
  UPDATING_PROMO = 'UPDATING_PROMO',
  ERROR = 'ERROR',
}

// ============================================================================
// CORE CART INTERFACES
// ============================================================================

export interface ICartItem {
  _id: string;
  description: RawDraftContentState;
  discountedPrice: number;
  discountAmount: number; // Per-unit discount amount (unitPrice - discountedPrice) - calculated by backend
  eventTicketId?: string;
  eventName?: string; // Optional event name for grouping
  isOnSale: boolean;
  itemId: string;
  eventId?: string;
  /** Collection pricing id or other-item line id (COLLECTION / OTHER_ITEM). */
  otherItemId?: string;
  membershipOrderId?: string;
  name: string;
  planOptions: PlanTypeEnum[];
  planType: PlanTypeEnum;
  quantity: number;
  saleLabel: string;
  total: number;
  type: CartItemTypeEnum;
  unitPrice: number;
  subTotal: number;
  checkoutAttendeesDetails?: string[]; // Array of attendee detail IDs
}

export interface IOrderSummary {
  discount: number;
  discountCode: string;
  eventTicketTotal: string;
  membershipDetails: {
    membershipName: string;
    unitPrice: number;
  };
  membershipTotal: number;
  subTotal: number;
  ticketDetails: {
    quantity: number;
    ticketName: string;
    unitPrice: number;
  }[];
  total: number;
  // Credit fields — computed at runtime, not stored
  creditAvailable?: number;
  creditApplied?: number;
  effectiveTotal?: number;
  unusedAmount?: number;
  unusedAmountApplied?: number;
  creditIssued?: number;
}

// ============================================================================
// CART CONTEXT
// ============================================================================

// Legacy CartContextType - kept for backward compatibility
// New components should use useCart from @/state/cart/useCart
export interface CartContextType {
  items?: ICartItem[];
  orderSummary?: IOrderSummary;
  itemCount?: number;
  loading: boolean;
  isEmpty: boolean;
  addToCart: (item: AddItemToCartInput[]) => Promise<void>;
  removeFromCart: (cartId: string, itemType: CartItemTypeEnum) => Promise<void>;
  updateQuantity: (updatedItem: UpdateItemQuantityInput) => Promise<void>;
  refreshCart: () => Promise<void>;
  clearCart: () => Promise<void>;
  appliedPromoCode?: string;
  availablePromoCodes?: IApplicablePromoCode[] | null;
  promoCodeLoading: boolean;
  applyPromoCode: (code: string) => Promise<void>;
}

// ============================================================================
// CART MUTATIONS (Re-export from global types + local extensions)
// ============================================================================

// Re-export from global types
export type {
  CreateCartOrderInput,
  UpdateItemMetadataInput,
  UpdateItemQuantityInput,
  GetCartInput,
  GetUserCartOrdersInput,
};

// New Cart API types
export interface ApplyPromocodeInput {
  promocode: string;
}

export interface CartOperationResponse {
  success: boolean;
  message?: string;
  error?: string;
  cart?: {
    _id: string;
    currentlyAppliedPromocode?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CartMutationResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface RemoveCartItemResponse extends CartMutationResponse {
  cartItems?: {
    _id: string;
    itemId: string;
  }[];
}

export interface UpdateCartItemResponse extends CartMutationResponse {
  removed?: boolean;
}

export interface CreateCartOrderResponse extends CartMutationResponse {
  clientSecret?: string;
  orderId?: string;
}

// ============================================================================
// CART QUERIES & RESPONSES (Re-export from global types + local extensions)
// ============================================================================

// Re-export from global types
export type { GetUserCartOrdersFilter };

export interface ICartMetadata {
  _id?: string;
  currentlyAppliedPromocode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICartSummary {
  itemsCount: number;
  eventTicketTotal: number;
  ticketDetails: ITicketDetail[];
  membershipTotal: number;
  membershipDetails?: IMembershipDetail;
  appliedPromocode?: string;
  promocodeDetails?: IPromocodeDetail;
  subTotal: number;
  discountAmount: number;
  total: number;
  taxAmount: number;
  taxRate: number;
  isValid: boolean;
  validationErrors?: string[];
}

export interface ITicketDetail {
  ticketName: string;
  unitPrice: number;
  quantity: number;
  eventId?: string;
  eventTicketId?: string;
}

export interface IMembershipDetail {
  membershipName?: string;
  unitPrice: number;
  planType?: 'MONTHLY' | 'ANNUALLY';
}

export interface IPromocodeDetail {
  code: string;
  description?: string;
  userFacingTitle?: string;
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  discountValue: number;
  applicableEntityType: 'EVENTS' | 'MEMBERSHIP' | 'ALL';
}

export interface GetCartItemsApiResponse {
  items?: ICartItem[];
  orderSummary?: IOrderSummary;
  cartSummary?: ICartSummary;
  cartMetadata?: ICartMetadata;
}

export interface CartResponse {
  success: boolean;
  message?: string;
  result: GetCartItemsApiResponse;
  error?: string;
}

export interface IApplicablePromoCode {
  _id: string;
  code: string;
  description: string;
  userFacingTitle: string;
  visibility: string;
  applicableTiers: {
    discountType: string;
    discountValue: number;
    entityType: string;
    minQuantity: number;
  }[];
}

export interface PromoCodeResponse {
  success: boolean;
  message?: string;
  promoCodes: IApplicablePromoCode[];
  error?: string;
}

export interface CartOrderItem {
  _id: string;
  itemId: string;
  eventId?: string;
  eventTicketId?: string;
  membershipOrderId?: string;
  type: string;
  name: string;
  unitPrice: number;
  discountedPrice?: number;
  quantity: number;
  isOnSale: boolean;
  saleLabel?: string;
  subTotal: number;
  total: number;
  checkoutAttendeesDetails?: string[];
}

export interface TicketDetail {
  ticketName: string;
  quantity: number;
  unitPrice: number;
}

export interface MembershipDetail {
  membershipName?: string; // Optional to match GraphQL schema
  unitPrice?: number; // Optional to match GraphQL schema
}

export interface OrderSummary {
  eventTicketTotal: number;
  ticketDetails: TicketDetail[];
  membershipTotal: number;
  membershipDetails?: MembershipDetail; // Single object, not array, and optional
  discount: number;
  discountCode?: string;
  subTotal: number;
  total: number;
}

export interface CartOrderPendingPayment {
  paymentIntentId: string;
  amount: number;
  currency: string;
  initiatedAt?: string | null;
}

export interface CartOrder {
  _id: string;
  userId: string;
  tenantId: string;
  bundleId: string;
  status: string;
  items: CartOrderItem[];
  orderSummary: OrderSummary;
  paymentIntentId?: string;
  paymentIntentClientSecret?: string;
  paymentStatus: string;
  pendingPayment?: CartOrderPendingPayment | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface UserCartOrder {
  _id: string;
  bundleId: string;
  status: string;
  receiptPdfUrl: string;
  paymentStatus: string;
  items?: CartOrderItem[];
  orderSummary: {
    total: number;
    subTotal: number;
    discount: number;
    discountCode?: string;
    eventTicketTotal?: number;
    membershipTotal?: number;
    ticketDetails?: TicketDetail[];
    membershipDetails?: MembershipDetail;
  };
  payableAmount: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface UserCartOrdersResult {
  results: UserCartOrder[];
  totalCount: number;
  hasMore: boolean;
}

export interface PaymentMethodCard {
  _id: string;
  stripeExternalId: string;
  isDefault: boolean;
  verified: boolean;
  hasFailed: boolean;
  canCurrentUserRemoveThisPaymentMethod: boolean;
  creditCardLastFourDigits: string;
  expirationMonth: number;
  expirationYear: number;
  brand: string;
}

export interface PaymentMethodBankAccount {
  _id: string;
  stripeExternalId: string;
  isDefault: boolean;
  verified: boolean;
  hasFailed: boolean;
  canCurrentUserRemoveThisPaymentMethod: boolean;
  bankAccountLastFourDigits: string;
  bankName: string;
}

export type PaymentMethod = PaymentMethodCard | PaymentMethodBankAccount;

export interface CurrentUser {
  _id: string;
  paymentMethods: PaymentMethod[];
}

export interface ApplicableTier {
  discountType: string;
  discountValue: number;
  entityType: string;
  minQuantity: number;
}

export interface PromoCode {
  _id: string;
  code: string;
  description: string;
  userFacingTitle: string;
  visibility: string;
  applicableTiers: ApplicableTier[];
}

export interface CartItem {
  _id: string;
  description: string;
  discountedPrice?: number;
  eventTicketId?: string;
  isOnSale: boolean;
  itemId: string;
  membershipOrderId?: string;
  name: string;
  planOptions?: any;
  planType?: string;
  quantity: number;
  saleLabel?: string;
  total: number;
  type: string;
  unitPrice: number;
  subTotal: number;
}

export interface CartOrderSummary {
  discount: number;
  discountCode?: string;
  eventTicketTotal: number;
  membershipTotal: number;
  subTotal: number;
  total: number;
  membershipDetails: MembershipDetail[];
  ticketDetails: TicketDetail[];
}

// GetCartInput and GetUserCartOrdersInput are now imported from global types

export interface GetUserCartOrdersResponse {
  getUserCartOrders: {
    results: CartOrder[];
    totalCount: number;
    hasMore: boolean;
  };
}

export interface GetUserCartOrdersVariables {
  input: {
    limit?: number;
    offset?: number;
    filter?: {
      status?: string[];
      paymentStatus?: string[];
    };
  };
}

// ============================================================================
// CART HOOKS
// ============================================================================

export interface UseCheckoutHandlersProps {
  cartOrderId: string | null;
  setCartOrderId: (id: string | null) => void;
  setIsAwaitingOrderProcessing: (isAwaiting: boolean) => void;
}

// ============================================================================
// EVENT REGISTRATION
// ============================================================================

export type IEventItemsForCart =
  TicketSelect_EventFragment_ticketsCurrentUserCanPurchase & {
    eventId?: string | null; // Event ID for direct filtering
    eventTicketId?: string | null;
    itemId: string;
    itemType: CartItemType;
    membershipOrderId?: string | null;
    quantity: number;
  };

export type IEventTicketsCurrentUserCanPurchase = TicketSelect_EventFragment;

// IAddToCart is now replaced by AddItemToCartInput from global types

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface OrderSummaryProps {
  data?: IOrderSummary;
  showCheckoutButton?: boolean;
  checkoutButtonText?: string;
  onCheckoutClick?: () => void;
  isCheckoutDisabled?: boolean;
  // Show detailed breakdown of all items with name, quantity, price, etc.
  showDetailedSummary?: boolean;
}

export interface EventConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItem: ICartItem;
  onConfigured: () => void;
}

export interface CartOrderSummaryProps {
  orderId: string;
}

export interface CartOrdersListProps {
  limit?: number;
  offset?: number;
}

export interface CartPageLayoutProps {
  title?: string;
  showTitle?: boolean;
}

export interface CheckoutProps {
  items?: any[];
  orderSummary?: IOrderSummary;
  isLoading?: boolean;
  showOrderSummary?: boolean;
}

export interface CheckoutButtonProps extends Omit<ButtonProps, 'onClick'> {
  checkoutButtonText?: string;
}

export interface CartItemCardProps {
  item: ICartItem;
}

export interface CartItemProps {
  item: ICartItem | null;
  isLoading: boolean;
}

export interface CartItemDetailsProps {
  item: ICartItem;
  onQuantityChange: (newQuantity: number) => Promise<void>;
  isIncrementing?: boolean;
  isDecrementing?: boolean;
  onRemove: () => Promise<void>;
}

export interface CartItemConfigurationStatusProps {
  isConfigured: boolean;
  onConfigure: () => void;
}

export interface ItemDescriptionProps {
  description: any;
  isExpanded: boolean;
  onToggle: () => void;
}

export interface ItemHeaderProps {
  item: ICartItem;
}

export interface ItemQuantityControlsProps {
  item: ICartItem;
  isIncrementing: boolean;
  isDecrementing: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
}

export interface PricingDisplayProps {
  item: ICartItem;
  isOnSale: boolean;
  saleLabel: string;
}

export interface PriceRowProps {
  label: string;
  value: string | number | Dinero<number>;
  mr?: number;
  color?: string;
}

export interface RemoveItemButtonProps {
  onRemove: () => void;
  isLoading: boolean;
  itemId: string;
}

export interface SaleTagProps {
  isOnSale: boolean;
  saleLabel?: string;
}

export interface CartItemPricingProps {
  item: ICartItem;
}

export interface CartItemsListProps {
  items: ICartItem[];
  loading?: boolean;
}

// ============================================================================
// PAYMENT TYPES
// ============================================================================

export type PaymentMethodType = PaymentMethod_PaymentMethodFragment & {
  stripeExternalId: string;
};

export type CartOrderData = {
  clientSecret?: string; // Optional for free carts
  orderId: string;
  cartOrderId: string;
};

export type CartPaymentProps = {
  cartItems: any[];
  onSuccess: () => void;
  onAchPending?: (message: string) => void;
  onError: (error: string) => void;
  onOrderCreated?: (orderId: string) => void;
  onProcessingStart?: () => void;
};

export type { AddItemToCartInput };
